import { useNavigate, useParams } from '@tanstack/react-router';
import { memo, useCallback, useMemo } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { merge } from '@/components';
import { Button, Menu, MenuItem, Popover, Separator, Text, Tooltip, TooltipTrigger } from '@/components/core';
import { CheckmarkDuotoneIcon, EditIcon, HelpIcon, KnowledgeIcon, SettingsIcon, SlashIcon } from '@/components/icons';
import { DEFAULT_SELECTED_NODE } from '@/constants';
import {
  toAgentId,
  toAgentRunId,
  toValidationTaskId,
  useGetAgent,
  useGetAgentRun,
  useGetValidationTask,
} from '@/hooks/api';
import { useDocumentStore } from '@/store';
import { findModelId, findValidationTaskId } from '@/utils';

export type KnowledgeMenuProps = object;

// eslint-disable-next-line no-empty-pattern
const IKnowledgeMenu = ({}: KnowledgeMenuProps = {}) => {
  const status = useDocumentStore((state) => state.status);
  const setIsEditing = useDocumentStore((state) => state.setIsEditing);

  const { agentId, runId } = useParams({ strict: false });
  const { data: agent } = useGetAgent(toAgentId(agentId));
  const { data: run } = useGetAgentRun(toAgentRunId(agentId, runId));
  const { data: task } = useGetValidationTask(toValidationTaskId(null, findValidationTaskId(run)));

  const isInKnowledgeBase = useMemo(() => {
    const isCompleted = status === 'completed';
    const isRejected = task && task.status !== 'succeeded';
    return isCompleted && !isRejected;
  }, [status, task]);

  const navigate = useNavigate();

  const goToKnowledgeSettings = useCallback(async () => {
    if (!agent) return;

    const modelId = findModelId(agent);
    if (modelId) sessionStorage.setItem(DEFAULT_SELECTED_NODE, modelId);

    return navigate({ to: '/agents/$agentId/integration', params: { agentId: agent.agentId } });
  }, [agent, navigate]);

  return (
    <MenuTrigger>
      <TooltipTrigger>
        <Button
          excludeFromTabOrder
          variant="plain"
          className={merge(
            'items-center justify-center border p-1',
            isInKnowledgeBase ? 'border-primary-100' : 'border-gray-300'
          )}
        >
          <div className="relative">
            <KnowledgeIcon className={isInKnowledgeBase ? '!text-primary-500' : '!text-gray-400'} />
            {isInKnowledgeBase ? (
              <div className="bg-success-500 absolute right-0 bottom-0 z-50 !size-1.5 translate-x-0.75 translate-y-[2px] rounded-full ring-1 ring-white" />
            ) : null}
          </div>
        </Button>
        <Tooltip className="flex flex-col" variant="primary">
          <Text>{isInKnowledgeBase ? 'Added to AI knowledge' : 'Not in AI knowledge'}</Text>
          <Text className="!text-gray-400">Click to edit</Text>
        </Tooltip>
      </TooltipTrigger>
      <Popover className="flex flex-col gap-0 p-0 shadow-md">
        {isInKnowledgeBase ? (
          <div className="gap-sm flex items-center rounded-t-lg bg-gray-100 p-2">
            <CheckmarkDuotoneIcon variant="success" />
            <Text>Added to AI knowledge</Text>
          </div>
        ) : (
          <div className="gap-sm flex items-center rounded-t-lg bg-gray-100 p-2">
            <SlashIcon variant="error" />
            <Text>Not in AI knowledge</Text>
          </div>
        )}
        <Separator orientation="horizontal" />
        <Menu>
          <MenuItem isDisabled={!isInKnowledgeBase} onAction={() => setIsEditing?.(true)}>
            <EditIcon />
            <Text>Edit values</Text>
          </MenuItem>
          <MenuItem onAction={goToKnowledgeSettings}>
            <SettingsIcon />
            <Text>Knowledge settings</Text>
          </MenuItem>
          <Separator orientation="horizontal" />
          <MenuItem onAction={() => window.open('https://docs.cradl.ai/', '_blank')}>
            <HelpIcon />
            <Text className="!text-gray-500">Learn about AI knowledge</Text>
          </MenuItem>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
};

export const KnowledgeMenu = memo(IKnowledgeMenu) as typeof IKnowledgeMenu;
